import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DataPt, isoDate } from "./DataPt";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fmt, money, errMsg, newKey } from "./shared";


function erros(f, lote) {
  const e = {};
  const q = Number(f.qtd);
  if (!f.decisao) e.decisao = "Escolha a decisão";
  if (!(q > 0) || q > lote.saldo + 1e-6) e.qtd = `Informe de 0 a ${fmt(lote.saldo)}`;
  if (f.motivo.trim().length < 5) e.motivo = "Mínimo 5 caracteres";
  if (f.decisao === "LIBERAR") {
    if (!f.lote.trim()) e.lote = "Informe o lote identificado";
    if (!f.validade) e.validade = "Escolha a validade";
    else if (f.validade < isoDate(new Date())) e.validade = "Validade vencida";
  }
  return e;
}

export function AvaliarDialog({ lote, onClose, onDone }) {
  const [f, setF] = useState({ decisao: "", qtd: "", motivo: "", lote: "", validade: "" });
  const [busy, setBusy] = useState(false);
  const key = useRef(newKey());
  useEffect(() => { if (lote) { setF({ decisao: "", qtd: lote.saldo, motivo: "", lote: "", validade: "" }); key.current = newKey(); } }, [lote]);
  if (!lote) return null;
  const e = erros(f, lote);
  const ok = Object.keys(e).length === 0;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const salvar = async () => {
    if (!ok || busy) return;
    setBusy(true);
    try {
      const body = { idem_key: key.current, decisao: f.decisao, motivo: f.motivo.trim(), qtd: Number(f.qtd), ...(f.decisao === "LIBERAR" ? { lote: f.lote.trim(), validade: f.validade } : {}) };
      await api.post(`/producao/estoque/lotes/${lote.id}/avaliar`, body);
      toast.success("Avaliação registrada"); onDone();
    } catch (err) { toast.error(errMsg(err)); if (err.response?.status === 409) onDone(); } finally { setBusy(false); }
  };
  const Err = ({ k }) => (e[k] && (f[k] !== "" || ["decisao", "lote", "validade"].includes(k)) ? <div className="text-[11px] text-red-300" data-testid={`avaliar-erro-${k}`}>{e[k]}</div> : null);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="avaliar-dialog">
        <DialogHeader><DialogTitle className="font-display uppercase">Avaliar quarentena {lote.lote}</DialogTitle>
          <DialogDescription>{lote.item_nome} · saldo {fmt(lote.saldo)} {lote.unidade} · custo do lote {money(lote.custo_unit)} (mantido na liberação)</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Decisão</Label>
            <Select value={f.decisao} onValueChange={(v) => set("decisao", v)}><SelectTrigger data-testid="avaliar-decisao"><SelectValue placeholder="Escolha" /></SelectTrigger>
              <SelectContent><SelectItem value="LIBERAR">Liberar para expedição</SelectItem><SelectItem value="DESCARTAR">Descartar (baixa)</SelectItem></SelectContent></Select><Err k="decisao" /></div>
          <div><Label>Quantidade ({lote.unidade})</Label><Input type="number" value={f.qtd} onChange={(ev) => set("qtd", ev.target.value)} data-testid="avaliar-qtd" /><Err k="qtd" /></div>
          {f.decisao === "LIBERAR" && <>
            <div><Label>Lote identificado</Label><Input value={f.lote} onChange={(ev) => set("lote", ev.target.value)} data-testid="avaliar-lote" /><Err k="lote" /></div>
            <div><Label>Validade</Label>
              <DataPt value={f.validade} onChange={(v) => set("validade", v)} testid="avaliar-validade" />
              <Err k="validade" /></div>
          </>}
          <div className="col-span-2"><Label>Motivo / parecer do responsável</Label><Textarea rows={2} value={f.motivo} onChange={(ev) => set("motivo", ev.target.value)} data-testid="avaliar-motivo" /><Err k="motivo" /></div>
        </div>
        {f.decisao === "DESCARTAR" && <div className="text-xs text-amber-300">O descarte baixa a quantidade da quarentena no estoque logístico e da Produção, sem afetar outros lotes.</div>}
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={salvar} disabled={!ok || busy} data-testid="avaliar-confirmar">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Registrar avaliação</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
